import { Toast } from '@byecode/ui'
import retryEnhancer from '@rpldy/retry-hooks'
import type { BatchItem } from '@rpldy/uploady'
import Uploady, { FILE_STATES, UPLOADER_EVENTS, useUploady } from '@rpldy/uploady'
import { t } from 'i18next'
import { findIndex } from 'rambda'
import type { FC } from 'react'
import React, { useContext, useMemo, useRef } from 'react'
import { debounce } from 'throttle-debounce'

import { defaultFileMaxUploadSize, limitExceededCodeList } from '../../constants'
import { getFileSizeToMB } from '../../utils'
import type { UploadBatchCallbackParameter, UploadItemCallbackParameter } from './hooks'
import { uploadManageEvents } from './hooks/events'
import { TokenRefreshManagerProvider } from './TokenRefreshManagerContext'
import type { UploadManageContextProps } from './types'

export const defaultFileFilter = (file: File | string) => {
    if (typeof file === 'string') {
        return true
    }
    if (file.size > defaultFileMaxUploadSize) {
        Toast.error(`${t('fileExceedsUploadLimit', { v: getFileSizeToMB(defaultFileMaxUploadSize) })}`)
        return false
    }
    return true
}

const defaultUploadyProps: UploadManageContextProps = {
    concurrent: true,
    maxConcurrent: 5,
    enhancer: retryEnhancer as unknown as UploadManageContextProps['enhancer'],
    fileFilter: defaultFileFilter,
    isSuccessfulCall: xhr => {
        // 🔑 401 必须返回 false，这样 item 才会进入 ERROR 状态
        // 这样 retry() 才能重试
        // 我们通过过滤 useBatchErrorListener 来阻止业务错误回调
        if (xhr.status === 401) {
            return false
        }

        if (xhr.status === 200) {
            const res = JSON.parse(xhr.response)
            if (res.code === '0030000') {
                return false
            }
            if (limitExceededCodeList.includes(res.code)) {
                return false
            }
        }
        return true
    }
}

type UploadItemListenerType = {
    callback: (data: BatchItem) => void
    id: string
    type: FILE_STATES
}

/**
 * @description Item级别的文件监听器
 * @date 9/6/2023 - 5:55:24 PM
 *
 * @class UploadBatchListener
 * @typedef {UploadBatchListener}
 */
class UploadItemListener {
    #listeners: UploadItemListenerType[] = []

    constructor() {
        uploadManageEvents.on('item-uploading', debounce(30, this.#itemStart).bind(this))
        uploadManageEvents.on('item-finished', debounce(30, this.#itemFinished).bind(this))
        uploadManageEvents.on('item-error', debounce(30, this.#itemError).bind(this))

        // uploadManageEvents.on('batch-uploading', debounce(30, this.#batchStart).bind(this))
        // uploadManageEvents.on('batch-finished', debounce(30, this.#batchFinished).bind(this))
        // uploadManageEvents.on('batch-error', debounce(30, this.#batchError).bind(this))
    }

    #itemStart({ batchItem, id }: UploadItemCallbackParameter) {
        this.#listeners.forEach(ls => {
            if (ls.id === id && ls.type === FILE_STATES.UPLOADING) {
                ls.callback(batchItem)
            }
        })
    }

    #itemFinished({ batchItem, id }: UploadItemCallbackParameter) {
        this.#listeners.forEach(ls => {
            if (ls.id === id && ls.type === FILE_STATES.FINISHED) {
                ls.callback(batchItem)
            }
        })
    }

    #itemError({ batchItem, id }: UploadItemCallbackParameter) {
        this.#listeners.forEach(ls => {
            if (ls.id === id && ls.type === FILE_STATES.ERROR) {
                ls.callback(batchItem)
            }
        })
    }

    add(callback: (data: BatchItem) => void, groupId: string, type: FILE_STATES) {
        this.#listeners.push({ callback, id: groupId, type })
    }

    remove(groupId: string) {
        this.#listeners = this.#listeners.filter(item => item.id !== groupId)
    }

    clear() {
        this.#listeners = []
    }
}

type UploadBatchListenerType = {
    callback: (data: BatchItem[], batchId: string, targetId: string) => void
    id: string
    type: FILE_STATES
    batchItem?: BatchItem[]
    deleteIds: Set<string>
    targetId: string
}

/**
 * @description Batch级别的文件监听器
 * @date 9/6/2023 - 5:55:24 PM
 *
 * @class UploadBatchListener
 * @typedef {UploadBatchListener}
 */
class UploadBatchListener {
    #listeners: UploadBatchListenerType[] = []

    constructor() {
        const debouncedBatchStart = debounce(30, this.#batchStart.bind(this))
        uploadManageEvents.on('batch-uploading', debouncedBatchStart)
        uploadManageEvents.on('batch-progress', debouncedBatchStart)
        uploadManageEvents.on('batch-finished', debounce(30, this.#batchFinished).bind(this))
        uploadManageEvents.on('batch-error', debounce(30, this.#batchError).bind(this))
    }

    getBatches(groupId: string, type: FILE_STATES) {
        return this.#listeners?.find(item => item.id === groupId && item.type === type)
    }

    add(callback: (data: BatchItem[], batchId: string, targetId: string) => void, groupId: string, type: FILE_STATES, targetId?: string) {
        const tid = targetId || groupId
        const index = this.#listeners.findIndex(item => item.id === groupId && item.type === type)
        if (index >= 0) {
            const existingDeleteIds = this.#listeners[index].deleteIds
            this.#listeners[index] = { callback, id: groupId, type, deleteIds: existingDeleteIds, targetId: tid }
            return
        }
        this.#listeners.push({ callback, id: groupId, type, deleteIds: new Set(), targetId: tid })
    }

    remove(groupId: string) {
        this.#listeners = this.#listeners.filter(item => item.id !== groupId)
    }

    removeFinishBatchItem(groupId: string, batchItemIds: string[]) {
        const batch = this.#listeners?.find(item => item.id === groupId && item.type === FILE_STATES.FINISHED)
        if (!batch?.batchItem) {
            return
        }
        batchItemIds.forEach(id => {
            batch.deleteIds.add(id)
        })
        batch.batchItem = batch.batchItem.filter(item => !batchItemIds.includes(item.id))
    }

    removeUploadingBatchItem(groupId: string, batchItemIds: string[]) {
        const batch = this.#listeners?.find(item => item.id === groupId && item.type === FILE_STATES.UPLOADING)
        if (!batch?.batchItem) {
            return
        }
        batchItemIds.forEach(id => {
            batch.deleteIds.add(id)
        })
        batch.batchItem = batch.batchItem.filter(item => !batchItemIds.includes(item.id))
    }

    removeBatchItem(groupId: string, batchItemIds: string[]) {
        this.removeFinishBatchItem(groupId, batchItemIds)
        this.removeUploadingBatchItem(groupId, batchItemIds)
    }

    // useItemProgressListener
    #batchStart({ batch, id }: UploadBatchCallbackParameter) {
        this.#listeners.forEach(ls => {
            // batch-uploading/batch-progress 都复用 #batchStart。
            // 这里使用“当前 batch 快照”，不要和历史 ls.batchItem 合并，
            // 否则上一轮上传条目会残留并污染下一轮，导致重复显示。
            if (ls.id === id && (ls.type === FILE_STATES.UPLOADING || ls.type === FILE_STATES.PENDING)) {
                ls.batchItem = [...batch.items]

                // 过滤已删除的项
                const list = ls.batchItem.filter(item => !ls.deleteIds.has(item.id))
                if (list.length === 0) {
                    return
                }
                ls.callback([...list], batch.id, ls.targetId)
            }
        })
    }

    #batchFinished({ batch, id }: UploadBatchCallbackParameter) {
        this.#listeners.forEach(ls => {
            if (ls.id === id && ls.type === FILE_STATES.FINISHED) {
                const list = batch.items.filter(item => !ls.deleteIds.has(item.id))
                if (list.length === 0) {
                    return
                }
                ls.callback([...list], batch.id, ls.targetId)
            }
        })
    }

    // #batchProcess({ batch, id }: UploadBatchCallbackParameter) {
    //     this.#listeners.forEach(ls => {
    //         if (ls.id === id && ls.type === FILE_STATES.PENDING) {
    //             const list = ls.batchItem
    //             if (!list) {
    //                 ls.batchItem = batch.items
    //                 ls.callback(batch.items, batch.id)
    //                 return
    //             }
    //             batch.items.forEach(item => {
    //                 const index = findIndex(b => item.id === b.id, list)
    //                 if (index >= 0) {
    //                     return
    //                 }
    //                 list.push(item)
    //             })
    //             ls.callback(list, batch.id)
    //         }
    //     })
    // }

    #batchError({ batch, id }: UploadBatchCallbackParameter) {
        this.#listeners.forEach(ls => {
            if (ls.id === id && ls.type === FILE_STATES.ERROR) {
                const list = ls.batchItem
                if (!list) {
                    ls.batchItem = batch.items
                    ls.callback(batch.items, batch.id, ls.targetId)
                    return
                }
                batch.items.forEach(item => {
                    const index = findIndex(b => item.id === b.id, list)
                    if (index >= 0) {
                        return
                    }
                    list.push(item)
                })
                ls.callback([...list], batch.id, ls.targetId)
            }
        })
    }

    clear() {
        this.#listeners = []
    }
}

const defaultListenersContextValue = { itemListener: new UploadItemListener(), batchListener: new UploadBatchListener() }

const ListenersContext = React.createContext<{
    itemListener: UploadItemListener
    batchListener: UploadBatchListener
}>(defaultListenersContextValue)

export const useListenersContext = () => useContext(ListenersContext)

export const UploadManageContext: FC<UploadManageContextProps> = props => {
    const { tokenRefreshManager, isSuccessfulCall: customIsSuccessfulCall, ...uploadyProps } = props
    const listenersInstance = useRef({ itemListener: new UploadItemListener(), batchListener: new UploadBatchListener() })

    // 🔑 包装自定义的 isSuccessfulCall，确保 401 优先处理
    const wrappedIsSuccessfulCall = useMemo(() => {
        return (xhr: XMLHttpRequest) => {
            // 🔑 401 必须返回 false，这样 item 才会进入 ERROR 状态，retry() 才能工作
            if (xhr.status === 401) {
                return false
            }

            // 如果有自定义的 isSuccessfulCall，调用它
            if (customIsSuccessfulCall) {
                return customIsSuccessfulCall(xhr)
            }

            // 否则使用默认逻辑
            if (defaultUploadyProps.isSuccessfulCall) {
                return defaultUploadyProps.isSuccessfulCall(xhr)
            }
            return true
        }
    }, [customIsSuccessfulCall])

    return (
        <TokenRefreshManagerProvider tokenRefreshManager={tokenRefreshManager}>
            <ListenersContext.Provider value={listenersInstance.current}>
                <Uploady {...defaultUploadyProps} {...uploadyProps} isSuccessfulCall={wrappedIsSuccessfulCall} />
            </ListenersContext.Provider>
        </TokenRefreshManagerProvider>
    )
}
export const useUploadManageContext = () => useUploady()
